import { WeekRings } from '../WeekRings.js';
import { Achievements } from '../Achievements.js';
import { HouseholdGoal } from '../HouseholdGoal.js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MIN_WEIGH_INS_PER_WEEK } from '@kraftwerk/shared';
import type { ReviewPoint, WeekReview as Review } from '@kraftwerk/shared';
import { loadReviewSeries, loadWeekReview } from '../../db/review.js';
import { Sparkline } from '../Sparkline.js';
import { formatDay } from '../../format.js';
import { CoachEntry } from '../coach/Coach.js';

/**
 * Die letzten sieben Tage auf einer Seite: Essen, Training, Supplements,
 * Gewicht. Was fehlt, steht als „keine Angabe" da — eine Woche ohne Protokoll
 * hat keinen Proteinschnitt von 0.
 */
export function WeekReview(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [review, setReview] = useState<Review | null>(null);
  const [series, setSeries] = useState<ReviewPoint[] | null>(null);

  useEffect(() => {
    // Eine Momentaufnahme, neu beim Öffnen und beim Zurückkommen in die App.
    const load = () => {
      void loadWeekReview().then(setReview);
      void loadReviewSeries(12).then(setSeries);
    };
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  if (!review) return <p className="text-sm text-ink-muted">{t('session.loading')}</p>;

  const number = (value: number, digits = 0) =>
    value.toLocaleString(i18n.language, { maximumFractionDigits: digits });
  const day = (iso: string) => formatDay(iso, 'date', i18n.language);
  const { nutrition, training, supplements, weight } = review;

  return (
    <section aria-labelledby="review-heading" className="flex flex-col gap-3">
      <h2 id="review-heading" className="kw-kicker">
        {t('review.range', { from: day(review.from), to: day(review.to) })}
      </h2>

      <WeekRings size="large" />
      <HouseholdGoal />
      <Achievements />
      {/* KI-Coach (27.10.2026): eigenständig, ohne Anbieter unsichtbar. */}
      <CoachEntry />

      <article className="kw-tile flex flex-col gap-1">
        <h3 className="kw-kicker">{t('review.nutrition')}</h3>
        {nutrition.avgProtein === null || nutrition.avgKcal === null ? (
          <p className="text-sm text-ink-muted">{t('review.noFood')}</p>
        ) : (
          <>
            <p>
              <span className="kw-figure text-3xl">{number(nutrition.avgProtein)}</span>{' '}
              <span className="text-sm">{t('review.avgProtein')}</span>
            </p>
            <p className="text-sm">
              {t('review.avgKcal', { kcal: number(nutrition.avgKcal) })} ·{' '}
              {t('review.daysLogged', { count: nutrition.daysLogged })}
            </p>
            {nutrition.proteinGoalDays !== null ? (
              <p className="text-sm text-ink-muted">
                {t('review.proteinGoal', { count: nutrition.proteinGoalDays })}
              </p>
            ) : null}
          </>
        )}
      </article>

      <article className="kw-tile flex flex-col gap-1">
        <h3 className="kw-kicker">{t('review.training')}</h3>
        <p>
          <span className="kw-figure text-3xl">{training.sessions}</span>{' '}
          <span className="text-sm">
            {t('review.sessions', { count: training.sessions })} ·{' '}
            {t('review.previous', { value: training.previousSessions })}
          </span>
        </p>
        {training.volumeKg > 0 || training.previousVolumeKg > 0 ? (
          <p className="text-sm">
            {t('review.volume', { kg: number(training.volumeKg) })} ·{' '}
            {t('review.previous', { value: `${number(training.previousVolumeKg)} kg` })}
          </p>
        ) : null}
      </article>

      <article className="kw-tile flex flex-col gap-1">
        <h3 className="kw-kicker">{t('review.supplements')}</h3>
        {supplements.rate === null ? (
          <p className="text-sm text-ink-muted">{t('review.noIntakes')}</p>
        ) : (
          <>
            <p>
              <span className="kw-figure text-3xl">{Math.round(supplements.rate * 100)} %</span>{' '}
              <span className="text-sm">
                {t('review.taken', { taken: supplements.taken, due: supplements.due })}
              </span>
            </p>
            {supplements.skipped > 0 ? (
              <p className="text-sm text-ink-muted">
                {t('review.skipped', { count: supplements.skipped })}
              </p>
            ) : null}
          </>
        )}
      </article>

      <article className="kw-tile flex flex-col gap-1">
        <h3 className="kw-kicker">{t('review.weight')}</h3>
        {weight.avg7 === null ? (
          <p className="text-sm text-ink-muted">
            {t('journal.weightTooFew', { min: MIN_WEIGH_INS_PER_WEEK })}
          </p>
        ) : (
          <>
            <p>
              <span className="kw-figure text-3xl">{number(weight.avg7, 1)}</span>{' '}
              <span className="text-sm">{t('journal.weightAvg')}</span>
            </p>
            {weight.change !== null ? (
              <p className="text-sm">
                {t('journal.weightChange', {
                  change: `${weight.change > 0 ? '+' : weight.change < 0 ? '−' : '±'}${number(
                    Math.abs(weight.change),
                    1,
                  )}`,
                })}
              </p>
            ) : null}
          </>
        )}
      </article>

      {series ? <Trends series={series} /> : null}
    </section>
  );
}

/**
 * Zwölf Wochen als Linien — Lücken bleiben Lücken. Jede Linie nennt erste und
 * letzte Woche mit Wert, damit sie auch ohne Hinsehen etwas sagt.
 */
function Trends({ series }: { series: ReviewPoint[] }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const number = (value: number, digits = 0) =>
    value.toLocaleString(i18n.language, { maximumFractionDigits: digits });

  const rows: {
    key: string;
    label: string;
    values: (number | null)[];
    show: (value: number) => string;
  }[] = [
    {
      key: 'weight',
      label: t('review.trendWeight'),
      values: series.map((point) => point.weightKg),
      show: (value) => `${number(value, 1)} kg`,
    },
    {
      key: 'protein',
      label: t('review.trendProtein'),
      values: series.map((point) => point.avgProtein),
      show: (value) => `${number(value)} g`,
    },
    {
      key: 'volume',
      label: t('review.trendVolume'),
      // Eine Woche ohne Einheit hat wirklich 0 kg Volumen — das ist eine Angabe.
      values: series.map((point) => point.volumeKg),
      show: (value) => `${number(value)} kg`,
    },
    {
      key: 'supplements',
      label: t('review.trendSupplements'),
      values: series.map((point) =>
        point.supplementRate === null ? null : Math.round(point.supplementRate * 100),
      ),
      show: (value) => `${value} %`,
    },
  ];

  return (
    <article className="kw-tile flex flex-col gap-3">
      <h3 className="kw-kicker">{t('review.trends', { count: series.length })}</h3>
      {rows.map((row) => {
        const known = row.values
          .map((value, index) => ({ value, index }))
          .filter((entry): entry is { value: number; index: number } => entry.value !== null);
        const first = known[0];
        const last = known.at(-1);
        const summary =
          first && last
            ? t('review.trendSummary', {
                label: row.label,
                first: row.show(first.value),
                last: row.show(last.value),
                weeks: known.length,
              })
            : t('review.trendNone', { label: row.label });
        return (
          <div key={row.key} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span>{row.label}</span>
              <span className="kw-figure text-ink-muted">{last ? row.show(last.value) : '—'}</span>
            </div>
            {known.length >= 2 ? (
              <Sparkline values={row.values} summary={summary} />
            ) : (
              <p className="text-xs text-ink-muted">{t('review.trendTooFew')}</p>
            )}
          </div>
        );
      })}
    </article>
  );
}
